import styled from 'foundations-theming/utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import SegmentationFiltersInstanceContext from '../state/SegmentationFiltersInstanceContext';
import { css } from 'styled-components';
import SegmentationFiltersViewer from './viewer/SegmentationFiltersViewer';
import SegmentationFiltersEditor from './editor/SegmentationFiltersEditor';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Text } from 'foundations-components/components/Text';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Panel } from 'foundations-components/transitional/components/Panel';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { PanelHeader } from 'foundations-components/transitional/components/PanelHeader';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getSidebarWidth = useNarrowPanelFormat => useNarrowPanelFormat ? 320 : 450;
const getSidebarWidthStyling = sidebarWidth => css(["flex:1 0 ", "px;height:100%;width:", "px;"], sidebarWidth, sidebarWidth);
const getFiltersViewerContainer = sidebarWidth => styled.div.withConfig({
  displayName: "SegmentationFiltersSidePanelLayout__getFiltersViewerContainer",
  componentId: "sc-1l3qzq5-0"
})(["", ";display:flex;flex-direction:column;overflow:hidden;"], getSidebarWidthStyling(sidebarWidth));
const getFiltersEditorContainer = sidebarWidth => styled.div.withConfig({
  displayName: "SegmentationFiltersSidePanelLayout__getFiltersEditorContainer",
  componentId: "sc-1l3qzq5-1"
})(["", " border-left:1px solid ", ";overflow:hidden;&:empty{display:none;}"], getSidebarWidthStyling(sidebarWidth), ({
  theme
}) => theme.color['do-not-use-battleship']);
const PanelBodyContainer = styled.div.withConfig({
  displayName: "SegmentationFiltersSidePanelLayout__PanelBodyContainer",
  componentId: "sc-1l3qzq5-2"
})(["height:100%;"]);
const DefaultFiltersViewerContainer = getFiltersViewerContainer(getSidebarWidth(false));
const NarrowFiltersViewerContainer = getFiltersViewerContainer(getSidebarWidth(true));
const DefaultFiltersEditorContainer = getFiltersEditorContainer(getSidebarWidth(false));
const NarrowFiltersEditorContainer = getFiltersEditorContainer(getSidebarWidth(true));
const SegmentationFiltersSidePanelLayout = ({
  animate = true
}) => {
  const {
    filtersContainerRef,
    isInEditMode,
    isEditorClosed,
    getTrackerInstance,
    props: {
      baseFilterFamily,
      closePanel,
      onEditPanelOpenChange,
      uiMessages,
      useNarrowPanelFormat,
      useOnePanel
    }
  } = useContext(SegmentationFiltersInstanceContext);
  const showEditor = isInEditMode && !isEditorClosed && !useOnePanel;
  useEffect(() => {
    var _getTrackerInstance;
    (_getTrackerInstance = getTrackerInstance()) === null || _getTrackerInstance === void 0 || _getTrackerInstance.track('openedAdvancedFiltersPanel', {
      primaryObjectTypeId: baseFilterFamily,
      version: useOnePanel ? '2' : '1'
    });
    return () => {
      var _getTrackerInstance2;
      (_getTrackerInstance2 = getTrackerInstance()) === null || _getTrackerInstance2 === void 0 || _getTrackerInstance2.track('closedAdvancedFiltersPanel', {
        primaryObjectTypeId: baseFilterFamily,
        version: useOnePanel ? '2' : '1'
      });
    };
  }, [baseFilterFamily, useOnePanel, getTrackerInstance]);
  useEffect(() => {
    if (typeof onEditPanelOpenChange === 'function') {
      onEditPanelOpenChange(showEditor);
    }
  }, [onEditPanelOpenChange, showEditor]);
  const [FiltersViewerContainer, FiltersEditorContainer] = useMemo(() => {
    if (useOnePanel) {
      return [DefaultFiltersViewerContainer, DefaultFiltersEditorContainer];
    }
    if (useNarrowPanelFormat) {
      return [NarrowFiltersViewerContainer, NarrowFiltersEditorContainer];
    }
    return [DefaultFiltersViewerContainer, DefaultFiltersEditorContainer];
  }, [useNarrowPanelFormat, useOnePanel]);
  const closePanelLocal = useCallback(() => {
    if (typeof closePanel === 'function') {
      closePanel();
    }
  }, [closePanel]);
  return /*#__PURE__*/_jsxs(Panel, {
    animate: animate,
    width: "auto",
    "data-selenium-test": "segmentation-filters-panel",
    children: [/*#__PURE__*/_jsxs(PanelHeader, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: closePanelLocal,
        "data-selenium-test": "segmentation-filters-panel-close-button"
      }), /*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: (uiMessages === null || uiMessages === void 0 ? void 0 : uiMessages.panelHeaderLabel) || /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.sidePanelLayout.panelTitle"
        })
      })]
    }), /*#__PURE__*/_jsx(PanelBody, {
      children: /*#__PURE__*/_jsx(PanelBodyContainer, {
        children: /*#__PURE__*/_jsxs(TFlex, {
          blockSize: "100%",
          minInlineSize: `${getSidebarWidth(useNarrowPanelFormat)}px`,
          ref: filtersContainerRef,
          children: [/*#__PURE__*/_jsx(FiltersViewerContainer, {
            children: /*#__PURE__*/_jsx("div", {
              style: {
                flexGrow: 1,
                width: '100%',
                overflow: 'hidden'
              },
              children: /*#__PURE__*/_jsx(SegmentationFiltersViewer, {})
            })
          }), showEditor && /*#__PURE__*/_jsx(FiltersEditorContainer, {
            children: /*#__PURE__*/_jsx(SegmentationFiltersEditor, {})
          })]
        })
      })
    })]
  });
};
export default SegmentationFiltersSidePanelLayout;